/* ════════════════════════════════════════════════════════════
   Modèles de mail pour l'envoi d'un devis.

   Un modèle est un texte à trous : `{{nom}}`, `{{total}}`… Les trous
   sont remplis au moment de l'ouverture, à partir du devis affiché.

   Le modèle « Devis complet » est écrit en dur : c'est le mail
   historique, il ne doit pas pouvoir disparaître d'un mauvais clic.
   Les autres vivent en base et s'éditent depuis l'admin.
   ════════════════════════════════════════════════════════════ */

import {
  calcLigne, dateLivraisonSouhaitee, grandTotalOf, genererDevis, getProduitsListe,
  getQuantite, isFourrage, lienSuivi, phraseLivraison, PIED_CONTACT, recapDemande,
} from './devisUtils'

/* ── Variables ───────────────────────────────────────────────
   Décrites ici une seule fois : la page d'édition affiche cette
   liste, et le remplissage l'utilise. Pas de risque qu'une aide
   mentionne une variable qui n'existe plus. */
export const VARIABLES = [
  { cle: 'nom',            aide: 'Nom du client' },
  { cle: 'produits',       aide: 'Produits demandés, séparés par des virgules' },
  { cle: 'quantite',       aide: 'Quantité demandée' },
  { cle: 'detail',         aide: 'Détail chiffré ligne par ligne' },
  { cle: 'total',          aide: 'Total général HT, en CHF' },
  { cle: 'livraison',      aide: 'Paragraphe de délai, adapté à la date demandée' },
  { cle: 'date_souhaitee', aide: 'Date de livraison souhaitée par le client' },
  { cle: 'validite',       aide: 'Date de fin de validité de l’offre (15 jours)' },
  { cle: 'recap',          aide: 'Rappel de la demande, telle qu’il l’a remplie' },
  { cle: 'lien_suivi',     aide: 'Lien de suivi du devis en ligne' },
  { cle: 'pied',           aide: 'Coordonnées et signature' },
]

const jour = (d) => d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })

/* Valeurs disponibles pour un devis donné. */
export function contexte(q, lignes = [], modele = null) {
  const detail = lignes.map((l) => {
    const c = calcLigne(l)
    const f = isFourrage(l.produit)
    return `--- ${l.produit} ---
- Poids total approximatif : ${c.p > 0 ? `${c.p} T` : 'À définir'}
- Prix unitaire : ${c.pr.toFixed(2)} CHF/100kg
${f ? `- Taxe Fourrage : ${c.tx.toFixed(2)} CHF/100kg` : ''}
- Sous-total : ${c.totalPrix.toFixed(2)} CHF`
  }).join('\n\n')

  const demandee = dateLivraisonSouhaitee(q)

  return {
    nom: (q?.name || '').trim() || 'cher client',
    produits: getProduitsListe(q).join(', '),
    quantite: getQuantite(q) || 'À définir',
    detail,
    total: `${grandTotalOf(lignes).toFixed(2)} CHF`,
    livraison: phraseLivraison(q, lignes, modele?.phrases),
    date_souhaitee: demandee ? jour(demandee) : 'non précisée',
    validite: jour(new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)),
    recap: recapDemande(q),
    lien_suivi: lienSuivi(q),
    pied: PIED_CONTACT,
  }
}

/* Remplit les trous. Une variable inconnue est laissée telle quelle :
   mieux vaut voir `{{trucmuche}}` dans l'aperçu qu'un blanc silencieux
   au milieu d'un mail parti chez le client. */
export function remplir(texte, ctx) {
  return (texte || '').replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (brut, cle) => {
    const v = ctx[cle.toLowerCase()]
    return v == null ? brut : String(v)
  })
}

/* ── Modèles fournis ─────────────────────────────────────────
   `cle` marque ceux que le code connaît. Le premier n'est pas un
   texte à trous : c'est la fonction historique, conservée intacte. */
export const MODELE_DEVIS = {
  cle: 'devis',
  nom: 'Devis complet',
  description: 'Le mail habituel, avec le détail chiffré et le délai adapté',
  sujet: 'Votre devis achatpaille.com — {{produits}}',
}

/* Les quatre situations que distingue {{livraison}}. Un modèle peut
   réécrire chacune ; le choix entre elles reste au code. */
export const CAS_LIVRAISON = [
  { cle: 'fourrage',  label: 'Foin ou luzerne — liste d’attente' },
  { cle: 'tenable',   label: 'Date demandée réalisable' },
  { cle: 'proche',    label: 'Date demandée trop proche' },
  { cle: 'sans_date', label: 'Aucune date demandée' },
]

/* Variables utilisables à l'intérieur de ces phrases. */
export const VARIABLES_LIVRAISON = [
  { cle: 'date_souhaitee',    aide: 'Date demandée par le client' },
  { cle: 'date_au_plus_tot',  aide: 'Première date réalisable' },
  { cle: 'attente_fourrage',  aide: 'Fenêtre annoncée pour les fourrages' },
]

/* Proposés à la création depuis la page d'édition, pour ne pas
   partir d'une page blanche. */
export const MODELES_SUGGERES = [
  {
    nom: 'Offre courte',
    sujet: 'Votre offre achatpaille.com — {{produits}}',
    corps: `Bonjour {{nom}},

Merci pour votre demande. Voici notre offre pour {{produits}} :

{{detail}}

TOTAL GÉNÉRAL HT : {{total}}
(TVA non applicable - export Suisse)

{{livraison}}
Nous prenons en charge les formalités douanières.

Offre valable jusqu'au {{validite}}.
🔍 Suivi de votre devis : {{lien_suivi}}

{{pied}}`,
  },
  {
    nom: 'Liste d’attente fourrage',
    sujet: 'Votre demande achatpaille.com — inscription sur liste d’attente',
    corps: `Bonjour {{nom}},

Merci pour votre demande de {{produits}}.

{{livraison}}

Voici l'offre que nous pouvons vous garantir dès qu'une date sera fixée :

{{detail}}

TOTAL GÉNÉRAL HT : {{total}}
(TVA non applicable - export Suisse)

🔍 Suivi de votre demande : {{lien_suivi}}

{{pied}}`,
  },
  {
    nom: 'Demande de précisions',
    sujet: 'Votre demande achatpaille.com — quelques précisions',
    corps: `Bonjour {{nom}},

{{recap}}

Avant de vous établir une offre ferme, j'aurais besoin d'une précision :

ÉCRIRE VOTRE QUESTION ICI

{{pied}}`,
  },
]

/* Corps final d'un modèle, prêt à partir. */
export function corpsDe(modele, q, lignes) {
  if (modele?.cle === 'devis') return genererDevis(q, lignes)
  return remplir(modele?.corps, contexte(q, lignes, modele))
}

export function sujetDe(modele, q, lignes) {
  return remplir(modele?.sujet || 'achatpaille.com', contexte(q, lignes, modele))
}

export function mailtoDe(modele, q, lignes) {
  return `mailto:${q.email}`
    + `?subject=${encodeURIComponent(sujetDe(modele, q, lignes))}`
    + `&body=${encodeURIComponent(corpsDe(modele, q, lignes))}`
}
